import type { TableConfigKey } from '@/generated/entity-config'
import type { EntityTableConfig } from './entity-table-config'

type MappingTableVariant = 'assigned' | 'assignable'

type TableConfigOverride = Partial<
	Pick<
		EntityTableConfig,
		| 'columns'
		| 'allowedColumnKeys'
		| 'actions'
		| 'defaultColumnOrder'
		| 'defaultColumnVisibility'
	>
>

type MappingTableConfigOverrideKey =
	`${TableConfigKey}-${TableConfigKey}-${MappingTableVariant}`

const defaultColumnOrderDisplayNameDescription = ['displayName', 'description']
const defaultColumnOrderCustomPageName = ['customPageName']
const defaultColumnOrderName = ['name']
const defaultColumnOrderNameUserCount = ['name', 'userCount']
const defaultColumnOrderFirstNameLastNameEmail = [
	'firstName',
	'lastName',
	'email',
]
const defaultColumnOrderSkillGroupName = ['skillGroupName']
const defaultColumnOrderSkillName = ['skillName']
const defaultColumnOrderTeamName = ['teamName']
const defaultColumnOrderUserFirstLastEmail = [
	'userFirstName',
	'userLastName',
	'userEmail',
]

const defaultColumnVisibilityAssignableUsers = {
	extension: false,
	externalId: false,
	id: false,
	isDisabled: false,
	logonName: false,
}

const defaultColumnVisibilityAssignableTeams = {
	customPageCount: false,
	id: false,
	isDefaultTeam: false,
	modifiedBy: false,
	modifiedUtc: false,
	queueCount: false,
	responsibleUserGroup: false,
	skillCount: false,
	skillGroupCount: false,
	userGroup: false,
}

const defaultColumnVisibilityAssignableSkillGroups = {
	id: false,
	modifiedBy: false,
	modifiedUtc: false,
	skillCount: false,
	teamCount: false,
}

const defaultColumnVisibilityAssignableSkills = {
	id: false,
	modifiedBy: false,
	modifiedUtc: false,
	skillGroupCount: false,
	teamCount: false,
	templateCount: false,
	userCount: false,
}

const defaultColumnVisibilityAssignableCustomPages = {
	id: false,
	url: false,
	icon: false,
	enabledForAllUsers: false,
	sortOrder: false,
	linkTarget: false,
	iFrameWidth: false,
	modifiedBy: false,
	modifiedUtc: false,
}

const defaultColumnVisibilityCustomPagesAssigned = {
	customPageId: false,
	id: false,
	teamId: false,
	userEmail: false,
	userExternalId: false,
	userFirstName: false,
	userId: false,
	userLastName: false,
	userLogonName: false,
}

const defaultColumnVisibilityTeamPauseReasonsAssigned = {
	id: false,
	pauseReasonColor: false,
	pauseReasonId: false,
	pauseReasonName: false,
	teamId: false,
}

const defaultColumnVisibilityTeamSkillsAssigned = {
	id: false,
	isInherited: false,
	skillGroupId: false,
	skillId: false,
	teamId: false,
}

const mappingTableConfigOverridesByKey: Partial<
	Record<MappingTableConfigOverrideKey, TableConfigOverride>
> = {
	// #region Users

	// users -> customPages
	'users-customPages-assignable': {
		defaultColumnOrder: defaultColumnOrderDisplayNameDescription,
		defaultColumnVisibility: defaultColumnVisibilityAssignableCustomPages,
	},
	'users-customPages-assigned': {
		defaultColumnOrder: defaultColumnOrderCustomPageName,
		defaultColumnVisibility: defaultColumnVisibilityCustomPagesAssigned,
	},

	// users -> pauseReasons
	'users-pauseReasons-assignable': {
		defaultColumnOrder: ['name', 'userCount'],
		defaultColumnVisibility: {
			id: false,
			color: false,
			modifiedBy: false,
			modifiedUtc: false,
			sortOrder: false,
			teamCount: false,
		},
	},
	'users-pauseReasons-assigned': {
		defaultColumnOrder: ['pauseReasonName'],
		defaultColumnVisibility: {
			id: false,
			pauseReasonColor: false,
			pauseReasonId: false,
			teamId: false,
			userEmail: false,
			userFirstName: false,
			userId: false,
			userLastName: false,
			userLogonName: false,
		},
	},

	// users -> queues
	'users-queues-assignable': {
		defaultColumnOrder: ['name', 'description', 'type', 'userCount'],
		defaultColumnVisibility: {
			contactCodeTreeCount: false,
			id: false,
			modifiedBy: false,
			modifiedUtc: false,
			sortOrder: false,
			wrapUpTimeSeconds: false,
		},
	},
	'users-queues-assigned': {
		defaultColumnOrder: [
			'queueName',
			'queueDescription',
			'type',
			'isRequired',
			'isActive',
		],
		defaultColumnVisibility: {
			id: false,
			modifiedBy: false,
			queueId: false,
			teamId: false,
			userEmail: false,
			userExternalId: false,
			userFirstName: false,
			userId: false,
			userLastName: false,
			userLogonName: false,
		},
	},

	// users -> roles
	'users-roles-assignable': {
		defaultColumnOrder: defaultColumnOrderNameUserCount,
		defaultColumnVisibility: {
			applicationScope: false,
			entityScope: false,
			id: false,
			isBaseRole: false,
			isDefault: false,
			modifiedBy: false,
			modifiedUtc: false,
			permissionCount: false,
			templateCount: false,
			userGroup: false,
		},
	},
	'users-roles-assigned': {
		defaultColumnOrder: ['roleName'],
		defaultColumnVisibility: {
			id: false,
			isManuallyAssigned: false,
			isRoleDefault: false,
			roleApplicationScope: false,
			roleEntityScope: false,
			roleId: false,
			userEmail: false,
			userFirstName: false,
			userId: false,
			userLastName: false,
			userLogonName: false,
		},
	},

	// users -> roles
	'users-skillGroups-assignable': {
		defaultColumnOrder: defaultColumnOrderNameUserCount,
		defaultColumnVisibility: defaultColumnVisibilityAssignableSkillGroups,
	},
	'users-skillGroups-assigned': {
		defaultColumnOrder: ['skillGroupName', 'isActive'],
		defaultColumnVisibility: {
			id: false,
			modifiedBy: false,
			skillGroupId: false,
			teamId: false,
			userEmail: false,
			userFirstName: false,
			userId: false,
			userLastName: false,
			userLogonName: false,
		},
	},

	// users -> skills
	'users-skills-assignable': {
		defaultColumnOrder: defaultColumnOrderNameUserCount,
		defaultColumnVisibility: defaultColumnVisibilityAssignableSkills,
	},
	'users-skills-assigned': {
		defaultColumnOrder: ['skillName', 'skillLevel'],
		defaultColumnVisibility: {
			id: false,
			skillGroupId: false,
			skillId: false,
			teamId: false,
			userEmail: false,
			userFirstName: false,
			userId: false,
			userLastName: false,
			userLogonName: false,
		},
	},

	// users -> teams
	'users-teams-assignable': {
		defaultColumnOrder: defaultColumnOrderNameUserCount,
		defaultColumnVisibility: defaultColumnVisibilityAssignableTeams,
	},
	'users-teams-assigned': {
		defaultColumnOrder: ['teamName', 'isResponsible', 'isPrimaryTeam'],
		defaultColumnVisibility: {
			id: false,
			teamId: false,
			userEmail: false,
			userFirstName: false,
			userId: false,
			userLastName: false,
			userLogonName: false,
		},
	},

	// #endregion Users

	// #region Teams

	// teams -> customPages
	'teams-customPages-assignable': {
		defaultColumnOrder: defaultColumnOrderDisplayNameDescription,
		defaultColumnVisibility: defaultColumnVisibilityAssignableCustomPages,
	},
	'teams-customPages-assigned': {
		defaultColumnOrder: defaultColumnOrderCustomPageName,
		defaultColumnVisibility: defaultColumnVisibilityCustomPagesAssigned,
	},

	// teams -> pauseReasons
	'teams-pauseReasons-assignable': {
		defaultColumnOrder: defaultColumnOrderNameUserCount,
		defaultColumnVisibility: {
			color: false,
			id: false,
			modifiedBy: false,
			modifiedUtc: false,
			sortOrder: false,
			teamCount: false,
		},
	},
	'teams-pauseReasons-assigned': {
		defaultColumnOrder: ['pauseReasonName'],
		defaultColumnVisibility: {
			id: false,
			pauseReasonColor: false,
			pauseReasonId: false,
			teamId: false,
			teamName: false,
		},
	},

	// teams -> queues
	'teams-queues-assignable': {
		defaultColumnOrder: ['name', 'description', 'type', 'userCount'],
		defaultColumnVisibility: {
			contactCodeTreeCount: false,
			id: false,
			modifiedBy: false,
			modifiedUtc: false,
			sortOrder: false,
			wrapUpTimeSeconds: false,
		},
	},
	'teams-queues-assigned': {
		defaultColumnOrder: [
			'queueName',
			'queueDescription',
			'queueType',
			'isRequired',
			'isActive',
		],
		defaultColumnVisibility: {
			id: false,
			queueId: false,
			teamId: false,
			teamName: false,
		},
	},

	// teams -> skillGroups
	'teams-skillGroups-assignable': {
		defaultColumnOrder: defaultColumnOrderNameUserCount,
		defaultColumnVisibility: defaultColumnVisibilityAssignableSkillGroups,
	},
	'teams-skillGroups-assigned': {
		defaultColumnOrder: defaultColumnOrderSkillGroupName,
		defaultColumnVisibility: {
			id: false,
			skillGroupId: false,
			teamId: false,
			teamName: false,
		},
	},

	// teams -> skills
	'teams-skills-assignable': {
		defaultColumnOrder: defaultColumnOrderNameUserCount,
		defaultColumnVisibility: defaultColumnVisibilityAssignableSkills,
	},
	'teams-skills-assigned': {
		defaultColumnOrder: defaultColumnOrderSkillName,
		defaultColumnVisibility: defaultColumnVisibilityTeamSkillsAssigned,
	},

	// teams -> users
	'teams-users-assignable': {
		defaultColumnOrder: defaultColumnOrderFirstNameLastNameEmail,
		defaultColumnVisibility: defaultColumnVisibilityAssignableUsers,
	},
	'teams-users-assigned': {
		defaultColumnOrder: [
			'userFirstName',
			'userLastName',
			'userEmail',
			'isResponsible',
		],
		defaultColumnVisibility: {
			id: false,
			isPrimaryTeam: false,
			teamId: false,
			teamName: false,
			userId: false,
			userLogonName: false,
		},
	},

	// #endregion Teams

	// #region Roles

	// roles -> permissions
	'roles-permissions-assignable': {
		defaultColumnOrder: defaultColumnOrderName,
		defaultColumnVisibility: {
			id: false,
			modifiedBy: false,
			modifiedUtc: false,
			roleCount: false,
		},
	},
	'roles-permissions-assigned': {
		defaultColumnOrder: ['permissionName'],
		defaultColumnVisibility: {
			id: false,
			permissionId: false,
			roleId: false,
			roleName: false,
		},
	},

	// roles -> users
	'roles-users-assignable': {
		defaultColumnOrder: defaultColumnOrderFirstNameLastNameEmail,
		defaultColumnVisibility: defaultColumnVisibilityAssignableUsers,
	},
	'roles-users-assigned': {
		defaultColumnOrder: defaultColumnOrderUserFirstLastEmail,
		defaultColumnVisibility: {
			id: false,
			isManuallyAssigned: false,
			isRoleDefault: false,
			roleApplicationScope: false,
			roleEntityScope: false,
			roleId: false,
			roleName: false,
			userId: false,
			userLogonName: false,
		},
	},

	// #endregion Roles

	// #region Skills

	// skills -> skillGroups
	'skills-skillGroups-assignable': {
		defaultColumnOrder: defaultColumnOrderNameUserCount,
		defaultColumnVisibility: defaultColumnVisibilityAssignableSkillGroups,
	},
	'skills-skillGroups-assigned': {
		defaultColumnOrder: defaultColumnOrderSkillGroupName,
		defaultColumnVisibility: {},
	},

	// skills -> teams
	'skills-teams-assignable': {
		defaultColumnOrder: defaultColumnOrderNameUserCount,
		defaultColumnVisibility: {
			id: false,
			isDefaultTeam: false,
			modifiedBy: false,
			modifiedUtc: false,
			customPageCount: false,
			queueCount: false,
			skillGroupCount: false,
			skillCount: false,
			responsibleUserGroup: false,
			userGroup: false,
		},
	},
	'skills-teams-assigned': {
		defaultColumnOrder: ['teamName'],
		defaultColumnVisibility: defaultColumnVisibilityTeamSkillsAssigned,
	},

	// skills -> users
	'skills-users-assignable': {
		defaultColumnOrder: defaultColumnOrderFirstNameLastNameEmail,
		defaultColumnVisibility: defaultColumnVisibilityAssignableUsers,
	},
	'skills-users-assigned': {
		defaultColumnOrder: [
			'firstName',
			'lastName',
			'email',
			'skillLevel',
			'teams',
		],
		defaultColumnVisibility: {
			id: false,
			skillGroupId: false,
			skillId: false,
			skillName: false,
			teamId: false,
			userId: false,
			userLogonName: false,
		},
	},

	// #endregion

	// #region SkillGroups

	// skillGroups -> skills
	'skillGroups-skills-assignable': {
		defaultColumnOrder: defaultColumnOrderName,
		defaultColumnVisibility: {
			id: false,
			modifiedBy: false,
			modifiedUtc: false,
			skillGroupCount: false,
			teamCount: false,
			templateCount: false,
			userCount: false,
		},
	},
	'skillGroups-skills-assigned': {
		defaultColumnOrder: defaultColumnOrderSkillName,
		defaultColumnVisibility: {
			id: false,
			skillGroupId: false,
			skillGroupName: false,
			skillId: false,
		},
	},

	// skillGroups -> teams
	'skillGroups-teams-assignable': {
		defaultColumnOrder: ['name', 'skillGroupCount'],
		defaultColumnVisibility: {
			customPageCount: false,
			id: false,
			isDefaultTeam: false,
			modifiedBy: false,
			modifiedUtc: false,
			queueCount: false,
			responsibleUserGroup: false,
			skillCount: false,
			userCount: false,
			userGroup: false,
		},
	},
	'skillGroups-teams-assigned': {
		defaultColumnOrder: defaultColumnOrderTeamName,
		defaultColumnVisibility: {
			id: false,
			skillGroupId: false,
			skillGroupName: false,
			teamId: false,
		},
	},

	// skillGroups -> users
	'skillGroups-users-assignable': {
		defaultColumnOrder: defaultColumnOrderFirstNameLastNameEmail,
		defaultColumnVisibility: defaultColumnVisibilityAssignableUsers,
	},
	'skillGroups-users-assigned': {
		defaultColumnOrder: [
			'userFirstName',
			'userLastName',
			'userEmail',
			'isActive',
		],
		defaultColumnVisibility: {
			id: false,
			modifiedBy: false,
			skillGroupId: false,
			skillGroupName: false,
			teamId: false,
			userId: false,
			userLogonName: false,
		},
	},

	// #endregion SkillGroups

	// #region Queues

	// queues -> teams
	'queues-teams-assignable': {
		defaultColumnOrder: defaultColumnOrderNameUserCount,
		defaultColumnVisibility: defaultColumnVisibilityAssignableTeams,
	},
	'queues-teams-assigned': {
		defaultColumnOrder: ['teamName', 'isRequired', 'isActive'],
		defaultColumnVisibility: {
			id: false,
			queueDescription: false,
			queueId: false,
			teamId: false,
			queueName: false,
		},
	},

	// queues -> users
	'queues-users-assignable': {
		defaultColumnOrder: defaultColumnOrderFirstNameLastNameEmail,
		defaultColumnVisibility: defaultColumnVisibilityAssignableUsers,
	},
	'queues-users-assigned': {
		defaultColumnOrder: [
			'userFirstName',
			'userLastName',
			'userEmail',
			'isRequired',
			'isActive',
		],
		defaultColumnVisibility: {
			id: false,
			modifiedBy: false,
			queueDescription: false,
			queueId: false,
			queueName: false,
			teamId: false,
			type: false,
			userExternalId: false,
			userId: false,
			userLogonName: false,
		},
	},

	// #endregion Queues

	// #region CustomPages

	// customPages -> teams
	'customPages-teams-assignable': {
		defaultColumnOrder: defaultColumnOrderNameUserCount,
		defaultColumnVisibility: defaultColumnVisibilityAssignableTeams,
	},
	'customPages-teams-assigned': {
		defaultColumnOrder: defaultColumnOrderTeamName,
		defaultColumnVisibility: {
			customPageId: false,
			id: false,
			teamId: false,
			customPageName: false,
		},
	},

	// customPages -> users
	'customPages-users-assignable': {
		defaultColumnOrder: defaultColumnOrderFirstNameLastNameEmail,
		defaultColumnVisibility: defaultColumnVisibilityAssignableUsers,
	},
	'customPages-users-assigned': {
		defaultColumnOrder: defaultColumnOrderUserFirstLastEmail,
		defaultColumnVisibility: {
			customPageId: false,
			customPageName: false,
			id: false,
			teamId: false,
			userExternalId: false,
			userId: false,
			userLogonName: false,
		},
	},

	// #endregion CustomPages

	// #region Templates

	// templates -> teams
	'templates-teams-assignable': {
		defaultColumnOrder: defaultColumnOrderName,
		defaultColumnVisibility: {
			customPageCount: false,
			id: false,
			isDefaultTeam: false,
			modifiedBy: false,
			modifiedUtc: false,
			queueCount: false,
			responsibleUserGroup: false,
			skillCount: false,
			skillGroupCount: false,
			userCount: false,
			userGroup: false,
		},
	},
	'templates-teams-assigned': {
		defaultColumnOrder: defaultColumnOrderTeamName,
		defaultColumnVisibility: {
			id: false,
			teamId: false,
			templateId: false,
			templateName: false,
		},
	},

	// templates -> customPages
	'templates-customPages-assignable': {
		defaultColumnOrder: defaultColumnOrderDisplayNameDescription,
		defaultColumnVisibility: defaultColumnVisibilityAssignableCustomPages,
	},
	'templates-customPages-assigned': {
		defaultColumnOrder: defaultColumnOrderCustomPageName,
		defaultColumnVisibility: {
			id: false,
			templateId: false,
			customPageId: false,
			templateName: false,
		},
	},

	// templates -> skills
	'templates-skills-assignable': {
		defaultColumnOrder: defaultColumnOrderSkillName,
		defaultColumnVisibility: defaultColumnVisibilityAssignableSkills,
	},
	'templates-skills-assigned': {
		defaultColumnOrder: ['skillName'],
		defaultColumnVisibility: {
			id: false,
			templateId: false,
			skillId: false,
			templateName: false,
		},
	},

	// templates -> queues
	'templates-queues-assignable': {
		defaultColumnOrder: ['name', 'description', 'type'],
		defaultColumnVisibility: {
			id: false,
			wrapUpTimeSeconds: false,
			sortOrder: false,
			modifiedBy: false,
			modifiedUtc: false,
			userCount: false,
			contactCodeTreeCount: false,
		},
	},
	'templates-queues-assigned': {
		defaultColumnOrder: ['queueName', 'queueDescription'],
		defaultColumnVisibility: {
			id: false,
			templateId: false,
			queueId: false,
			templateName: false,
		},
	},

	// templates -> roles
	'templates-roles-assignable': {
		defaultColumnOrder: ['name'],
		defaultColumnVisibility: {
			id: false,
			modifiedBy: false,
			modifiedUtc: false,
			entityScope: false,
			applicationScope: false,
			isDefault: false,
			isBaseRole: false,
			userGroup: false,
			permissionCount: false,
			userCount: false,
			templateCount: false,
		},
	},
	'templates-roles-assigned': {
		defaultColumnOrder: ['roleName'],
		defaultColumnVisibility: {
			id: false,
			templateId: false,
			roleId: false,
			templateName: false,
		},
	},

	// #endregion Templates

	// #region Pause Reasons

	// pauseReasons -> teams
	'pauseReasons-teams-assignable': {
		defaultColumnOrder: defaultColumnOrderNameUserCount,
		defaultColumnVisibility: defaultColumnVisibilityAssignableTeams,
	},
	'pauseReasons-teams-assigned': {
		defaultColumnOrder: defaultColumnOrderTeamName,
		defaultColumnVisibility: defaultColumnVisibilityTeamPauseReasonsAssigned,
	},

	// pauseReasons -> users
	'pauseReasons-users-assignable': {
		defaultColumnOrder: defaultColumnOrderFirstNameLastNameEmail,
		defaultColumnVisibility: defaultColumnVisibilityAssignableUsers,
	},
	'pauseReasons-users-assigned': {
		defaultColumnOrder: defaultColumnOrderUserFirstLastEmail,
		defaultColumnVisibility: {
			id: false,
			pauseReasonColor: false,
			pauseReasonId: false,
			pauseReasonName: false,
			teamId: false,
			userId: false,
			userLogonName: false,
		},
	},

	// #endregion Pause Reasons

	// #region Custom Properties

	// customProperties -> ruleSets
	'customProperties-ruleSets-assignable': {
		defaultColumnOrder: ['name', 'description', 'customPropertyCount'],
		defaultColumnVisibility: {
			bold: false,
			color: false,
			contactColor: false,
			hideValue: false,
			id: false,
			isHidden: false,
			italic: false,
			link: false,
			linkDisplayName: false,
			maxWidth: false,
			minWidth: false,
			modifiedBy: false,
			modifiedUtc: false,
			openContactDetailBeforeAccepting: false,
			replaceValue: false,
			showInSplit: false,
			tabColor: false,
			underline: false,
		},
	},
	'customProperties-ruleSets-assigned': {
		defaultColumnOrder: ['ruleSetName', 'ruleSetDescription'],
		defaultColumnVisibility: {
			customPropertyId: false,
			customPropertyName: false,
			id: false,
			ruleSetId: false,
		},
	},

	// #endregion Custom Properties

	// #region Rule Sets

	// ruleSets -> customProperties
	'ruleSets-customProperties-assignable': {
		defaultColumnOrder: ['keyDisplayName', 'ruleSetsCount'],
		defaultColumnVisibility: {
			id: false,
			key: false,
			sortOrder: false,
			hidden: false,
			modifiedBy: false,
			modifiedUtc: false,
		},
	},
	'ruleSets-customProperties-assigned': {
		defaultColumnOrder: ['customPropertyName'],
		defaultColumnVisibility: {
			customPropertyId: false,
			id: false,
			ruleSetDescription: false,
			ruleSetId: false,
			ruleSetName: false,
		},
	},

	// #endregion Rule Sets
}

export const mappingTableConfigOverrides: Partial<
	Record<MappingTableConfigOverrideKey, TableConfigOverride>
> = Object.fromEntries(
	Object.entries(mappingTableConfigOverridesByKey).map(([key, override]) => [
		key,
		key.endsWith('-assigned')
			? {
					...override,
					allowedColumnKeys:
						override.allowedColumnKeys ?? override.defaultColumnOrder ?? [],
				}
			: override,
	]),
) as Partial<Record<MappingTableConfigOverrideKey, TableConfigOverride>>

export function getMappingTableConfigOverride({
	entity,
	relationKey,
	variant,
}: {
	entity: TableConfigKey
	relationKey: string
	variant: MappingTableVariant
}): TableConfigOverride | undefined {
	const key =
		`${entity}-${relationKey}-${variant}` as MappingTableConfigOverrideKey
	return mappingTableConfigOverrides[key]
}
